import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { inspirationAPI, playlistsAPI, videosAPI, filesAPI, userAPI, tagsAPI } from '../services/api';
import EditPlaylistModal from './EditPlaylistModal';
import AddInspirationModal from './AddInspirationModal';
import PrimaryButton from './PrimaryButton';
import HoverableCard from './HoverableCard';
import TagIcon from './TagIcon';
import ErrorState from './ErrorState';
import VideoThumbnail from './VideoThumbnail';
import UpgradeBanner from './UpgradeBanner';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import YouTubeImportPlaceholder from './YouTubeImportPlaceholder';
import { YOUTUBE_IMPORT_PARAM } from './YouTubePlaylistImport';
import { useYouTubeImport, useYouTubeImportRefresh } from '../context/YouTubeImportContext';
import { buildColorAlongVideoPath } from '../utils/colorAlongUtils';
import { STUDIO_SECTION_DESCRIPTIONS } from '../utils/studioSections';

const PLAYLIST_LG_PAGE_SIZE = 8;
const ITEM_LG_PAGE_SIZE = 10;
const FETCH_PAGE_SIZE = 10;
const SEARCH_FETCH_PAGE_SIZE = 100;

const useTwoRowPageSize = (lgCount) => {
  const [pageSize, setPageSize] = useState(lgCount);
  useEffect(() => {
    const update = () => {
      if (window.matchMedia('(min-width: 1024px)').matches) {
        setPageSize(lgCount);
      } else if (window.matchMedia('(min-width: 640px)').matches) {
        setPageSize(4);
      } else {
        setPageSize(2);
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [lgCount]);
  return pageSize;
};

const SeeMoreButton = ({ onClick, loading = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={loading}
    className="mt-4 w-full py-2.5 text-sm font-medium rounded-lg border border-slate-200 text-slate-600 hover:text-slate-800 hover:bg-slate-50 hover:border-slate-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
  >
    {loading ? 'Loading more...' : 'See more'}
  </button>
);

const Library = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { importing } = useYouTubeImport();
  const [videos, setVideos] = useState([]);
  const [files, setFiles] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  // Library-wide totals from the API (not affected by search/tag filters), used for free plan limits
  const [libraryCounts, setLibraryCounts] = useState({ videos: 0, files: 0, playlists: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [videoPage, setVideoPage] = useState(1);
  const [filePage, setFilePage] = useState(1);
  const [hasMoreVideos, setHasMoreVideos] = useState(true);
  const [hasMoreFiles, setHasMoreFiles] = useState(true);
  const [loadingMoreVideos, setLoadingMoreVideos] = useState(false);
  const [loadingMoreFiles, setLoadingMoreFiles] = useState(false);
  const [loadingPlaylists, setLoadingPlaylists] = useState(false);
  const [playlistPages, setPlaylistPages] = useState(1);
  const [videoPages, setVideoPages] = useState(1);
  const [filePages, setFilePages] = useState(1);
  const playlistPageSize = useTwoRowPageSize(PLAYLIST_LG_PAGE_SIZE);
  const itemPageSize = useTwoRowPageSize(ITEM_LG_PAGE_SIZE);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [addModalVideoMode, setAddModalVideoMode] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editPlaylistModalOpen, setEditPlaylistModalOpen] = useState(false);
  const [playlistToEdit, setPlaylistToEdit] = useState(null);
  const [playlistToDelete, setPlaylistToDelete] = useState(null);
  const [showPlaylistDeleteModal, setShowPlaylistDeleteModal] = useState(false);
  const [favorites, setFavorites] = useState(new Set());
  const [togglingFavorite, setTogglingFavorite] = useState(null);
  const [tagFilterIds, setTagFilterIds] = useState([]);
  const [knownTags, setKnownTags] = useState([]);
  const [tagFilterDropdownOpen, setTagFilterDropdownOpen] = useState(false);
  const [tagFilterSearch, setTagFilterSearch] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [debouncedItemSearch, setDebouncedItemSearch] = useState('');
  const [matchingPlaylistIds, setMatchingPlaylistIds] = useState(null);
  const tagFilterDropdownRef = useRef(null);

  // Check if we should open the add modal from navigation state
  useEffect(() => {
    if (location.state?.openAddModal) {
      setIsAddModalOpen(true);
      // Clear the state to prevent reopening on re-render
      navigate(location.pathname, { replace: true, state: { ...location.state, openAddModal: false } });
    }
  }, [location.state, navigate, location.pathname]);

  const scrolledSectionRef = useRef(null);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedSection = params.get('section');
    if (requestedSection !== 'videos' && requestedSection !== 'files' && requestedSection !== 'playlists') return;
    if (loading || loadingPlaylists) return;
    if (scrolledSectionRef.current === requestedSection) return;
    const node = document.getElementById(`inspiration-${requestedSection}`);
    if (!node) return;
    scrolledSectionRef.current = requestedSection;
    node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.search, loading, loadingPlaylists]);

  // Back from connecting YouTube: reopen the playlist picker
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get(YOUTUBE_IMPORT_PARAM) !== '1') return;
    params.delete(YOUTUBE_IMPORT_PARAM);
    setAddModalVideoMode('youtube');
    setIsAddModalOpen(true);
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true, state: location.state });
  }, [location.search, location.pathname, location.state, navigate]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedItemSearch(itemSearch.trim());
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [itemSearch]);

  // Transform API response to component format
  const transformItem = (item) => {
    // Ensure id is a number for consistent comparison with favorites
    const id = Number(item.id);
    const normalizedId = !isNaN(id) ? id : item.id;
    
    if (item.type === 'video') {
      return {
        id: normalizedId,
        type: 'video',
        title: item.title || 'Untitled Video',
        thumbnail: item.thumb || 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=400&h=300&fit=crop',
        videoId: item.embed_id,
        embedId: item.embed_id,
        pencilSetSizeId: item.colored_pencil_set_size_id ?? null,
        bookId: item.book_id ?? null,
        createdAt: item.created_at || item.createdAt || null,
        tags: item.tags || [],
      };
    } else if (item.type === 'file' || item.type === 'image' || item.type === 'pdf') {
      // Determine file type from mime_type or use the type from API
      const isPdf = item.type === 'pdf' || item.mime_type?.includes('pdf');
      const isImage = item.type === 'image' || item.mime_type?.startsWith('image/');
      
      return {
        id: normalizedId,
        type: isPdf ? 'pdf' : (isImage ? 'image' : 'file'),
        title: item.title || 'Untitled File',
        thumbnail: item.thumbnail_path || item.path || 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=400&h=300&fit=crop',
        path: item.path,
        mime_type: item.mime_type,
        tags: item.tags || [],
      };
    }
    return null;
  };


  const transformPreviewItem = useCallback((item) => {
    const id = Number(item.id);
    const normalizedId = !isNaN(id) ? id : item.id;
    if (item.type === 'video') {
      return {
        id: normalizedId,
        type: 'video',
        title: item.title || 'Untitled Video',
        thumbnail: item.thumb || `https://img.youtube.com/vi/${item.embed_id}/hqdefault.jpg`,
        videoId: item.embed_id,
        embedId: item.embed_id,
        pencilSetSizeId: item.colored_pencil_set_size_id ?? null,
        bookId: item.book_id ?? null,
      };
    }
    return {
      id: normalizedId,
      type: item.type || 'file',
      title: item.title || 'Untitled File',
      thumbnail: item.thumbnail_path || item.path || 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=400&h=300&fit=crop',
      path: item.path,
      mime_type: item.mime_type,
    };
  }, []);


  // Fetch playlists (preview: first 5 items per playlist from API)
  const fetchPlaylists = useCallback(async () => {
    try {
      setLoadingPlaylists(true);
      setError(null);

      const response = await playlistsAPI.getAll({ preview: true });
      const playlistsData = Array.isArray(response) ? response : (response?.data ?? []);

      const transformedPlaylists = playlistsData.map((playlist) => {
        const playlistId = Number(playlist.id);
        const base = {
          id: !isNaN(playlistId) ? playlistId : playlist.id,
          title: playlist.title || 'Untitled Playlist',
          thumbnail: playlist.thumb || 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=400&h=300&fit=crop',
          videos: [],
          files: [],
        };
        if (playlist.preview_items && playlist.preview_items.length > 0) {
          base.preview_items = playlist.preview_items.map(transformPreviewItem);
          base.preview_total = playlist.preview_total ?? playlist.preview_items.length;
        }
        if (playlist.videos && playlist.videos.length > 0) {
          base.videos = (playlist.videos || []).map((video) => ({
            id: Number(video.id) || video.id,
            type: 'video',
            title: video.title || 'Untitled Video',
            thumbnail: video.thumb || `https://img.youtube.com/vi/${video.embed_id}/hqdefault.jpg`,
            videoId: video.embed_id,
            embedId: video.embed_id,
            pencilSetSizeId: video.colored_pencil_set_size_id ?? null,
            bookId: video.book_id ?? null,
            createdAt: video.created_at || video.createdAt || null,
          }));
        }
        if (playlist.files && playlist.files.length > 0) {
          const isPdf = (f) => f.mime_type && f.mime_type.includes('pdf');
          const isImage = (f) => f.mime_type && f.mime_type.startsWith('image/');
          base.files = (playlist.files || []).map((f) => ({
            id: Number(f.id) || f.id,
            type: isPdf(f) ? 'pdf' : (isImage(f) ? 'image' : 'file'),
            title: f.title || 'Untitled File',
            thumbnail: f.thumbnail_path || f.path || 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=400&h=300&fit=crop',
            path: f.path,
            mime_type: f.mime_type,
          }));
        }
        return base;
      });

      setPlaylists(transformedPlaylists);
    } catch (err) {
      setError(err.message || err.data?.message || 'Failed to load playlists');
      console.error('Error fetching playlists:', err);
      setPlaylists([]);
    } finally {
      setLoadingPlaylists(false);
    }
  }, [transformPreviewItem]);

  const loadInspirationType = useCallback(async (type, page = 1, append = false) => {
    const filters = {
      type,
      sort: type === 'video' ? 'created_at' : 'title',
      sort_direction: type === 'video' ? 'desc' : 'asc'
    };
    if (debouncedItemSearch) {
      filters.search = debouncedItemSearch;
    }
    if (tagFilterIds.length > 0) {
      filters.tags = tagFilterIds;
    }
    const perPage = debouncedItemSearch ? SEARCH_FETCH_PAGE_SIZE : FETCH_PAGE_SIZE;
    const response = await inspirationAPI.getAll(page, perPage, filters);
    const transformedItems = (response.data || []).map(transformItem).filter(Boolean);
    if (debouncedItemSearch && response.current_page < response.last_page) {
      const extraResponses = await Promise.all(
        Array.from({ length: response.last_page - response.current_page }, (_, index) => (
          inspirationAPI.getAll(response.current_page + index + 1, perPage, filters)
        ))
      );
      extraResponses.forEach((extraResponse) => {
        transformedItems.push(...(extraResponse.data || []).map(transformItem).filter(Boolean));
      });
    }
    const hasMorePages = !debouncedItemSearch && response.current_page < response.last_page;
    if (type === 'file') {
      setFiles((prev) => (append ? [...prev, ...transformedItems] : transformedItems));
      setHasMoreFiles(hasMorePages);
      setFilePage(debouncedItemSearch ? response.last_page : response.current_page);
    } else {
      setVideos((prev) => (append ? [...prev, ...transformedItems] : transformedItems));
      setHasMoreVideos(hasMorePages);
      setVideoPage(debouncedItemSearch ? response.last_page : response.current_page);
    }
    if (response.meta?.counts) {
      setLibraryCounts(response.meta.counts);
    }
  }, [tagFilterIds, debouncedItemSearch]);

  const fetchInspirations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await Promise.all([
        loadInspirationType('video', 1, false),
        loadInspirationType('file', 1, false),
      ]);
    } catch (err) {
      setError(err.message || err.data?.message || 'Failed to load inspirations');
      console.error('Error fetching inspirations:', err);
      setVideos([]);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [loadInspirationType]);

  // Fetch user favorites
  const fetchFavorites = useCallback(async () => {
    try {
      const userId = user?.id;
      if (!userId) return;
      
      if (!userAPI.getFavorites) {
        setFavorites(new Set());
        return;
      }
      
      const response = await userAPI.getFavorites(userId);
      
      // Handle different response structures - be more flexible
      let favoritesData = [];
      
      // Check if response is directly an array
      if (Array.isArray(response)) {
        favoritesData = response;
      } 
      // Check if response has a data property that's an array
      else if (response?.data && Array.isArray(response.data)) {
        favoritesData = response.data;
      }
      // Check if response is array-like (has length property)
      else if (response && typeof response === 'object' && 'length' in response && typeof response.length === 'number') {
        // Convert array-like object to array
        favoritesData = Array.from(response);
      }
      // Last resort: try to use response directly if it exists
      else if (response) {
        favoritesData = response;
      }
      
      // Ensure favoritesData is actually an array
      if (!Array.isArray(favoritesData)) {
        favoritesData = Array.isArray(favoritesData) ? favoritesData : (favoritesData ? [favoritesData] : []);
      }
      
      if (favoritesData.length > 0) {
        const favoriteIds = new Set();
        favoritesData.forEach(fav => {
          // Check for both possible type formats and use flexible matching
          const isVideo = fav.favoritable_type === 'App\\Models\\Video' || 
                        fav.favoritable_type === 'App\Models\Video' ||
                        fav.favoritable_type?.includes('Video');
          const isFile = fav.favoritable_type === 'App\\Models\\File' || 
                        fav.favoritable_type === 'App\Models\File' ||
                        fav.favoritable_type?.includes('File');
          
          if (fav.favoritable_id !== undefined && fav.favoritable_id !== null) {
            // Convert to number to ensure type consistency
            const id = Number(fav.favoritable_id);
            if (!isNaN(id)) {
              if (isVideo) {
                favoriteIds.add(`video-${id}`);
              } else if (isFile) {
                favoriteIds.add(`file-${id}`);
              }
            }
          }
        });
        setFavorites(favoriteIds);
      } else {
        // If no favorites found, set empty Set
        setFavorites(new Set());
      }
    } catch (err) {
      console.error('Error fetching favorites:', err);
      setFavorites(new Set());
    }
  }, [user]);

  // Fetch known tags for filter dropdown
  useEffect(() => {
    let cancelled = false;
    tagsAPI.getAll().then((response) => {
      if (cancelled) return;
      const list = Array.isArray(response) ? response : (response?.data ?? []);
      setKnownTags(list);
    }).catch(() => { if (!cancelled) setKnownTags([]); });
    return () => { cancelled = true; };
  }, []);

  // Close tag filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (tagFilterDropdownRef.current && !tagFilterDropdownRef.current.contains(e.target)) {
        setTagFilterDropdownOpen(false);
      }
    };
    if (tagFilterDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [tagFilterDropdownOpen]);

  useEffect(() => {
    if (tagFilterIds.length === 0) {
      setMatchingPlaylistIds(null);
      return undefined;
    }
    setMatchingPlaylistIds(new Set());
    let cancelled = false;
    Promise.all([
      inspirationAPI.getAll(1, 100, { type: 'video', tags: tagFilterIds }),
      inspirationAPI.getAll(1, 100, { type: 'file', tags: tagFilterIds }),
      playlistsAPI.getAll(),
    ]).then(([videoResponse, fileResponse, playlistResponse]) => {
      if (cancelled) return;
      const keys = new Set();
      (videoResponse.data || []).forEach((item) => keys.add(`video-${Number(item.id)}`));
      (fileResponse.data || []).forEach((item) => keys.add(`file-${Number(item.id)}`));
      const playlistsData = Array.isArray(playlistResponse) ? playlistResponse : (playlistResponse?.data ?? []);
      const ids = new Set();
      playlistsData.forEach((playlist) => {
        const itemKeys = [
          ...(playlist.videos || []).map((item) => `video-${Number(item.id)}`),
          ...(playlist.files || []).map((item) => `file-${Number(item.id)}`),
        ];
        if (itemKeys.some((key) => keys.has(key))) {
          ids.add(Number(playlist.id));
        }
      });
      setMatchingPlaylistIds(ids);
    }).catch(() => {
      if (!cancelled) setMatchingPlaylistIds(new Set());
    });
    return () => { cancelled = true; };
  }, [tagFilterIds]);

  useEffect(() => {
    fetchInspirations();
    fetchFavorites();
  }, [fetchInspirations, fetchFavorites]);

  // Fetch playlists on mount (for Playlists view and Add to playlist dropdown)
  useEffect(() => {
    fetchPlaylists();
  }, [fetchPlaylists]);

  const refreshImportedVideos = useCallback(() => {
    fetchInspirations();
    fetchPlaylists();
  }, [fetchInspirations, fetchPlaylists]);
  useYouTubeImportRefresh(refreshImportedVideos);

  useEffect(() => {
    setPlaylistPages(1);
    setVideoPages(1);
    setFilePages(1);
  }, [debouncedItemSearch, tagFilterIds]);

  // Check if user has free plan
  const isFreePlan = user?.subscription_plan === 'free' || !user?.subscription_plan;
  const FREE_PLAN_LIMIT = 5;

  const limitedVideos = isFreePlan ? videos.slice(0, FREE_PLAN_LIMIT) : videos;
  const limitedFiles = isFreePlan ? files.slice(0, FREE_PLAN_LIMIT) : files;

  const videoCount = Math.max(libraryCounts.videos || 0, videos.length);
  const fileCount = Math.max(libraryCounts.files || 0, files.length);
  const playlistCount = Math.max(libraryCounts.playlists || 0, playlists.length);
  const hasReachedVideoLimit = isFreePlan && videoCount >= FREE_PLAN_LIMIT;
  const hasReachedFileLimit = isFreePlan && fileCount >= FREE_PLAN_LIMIT;
  const hasReachedPlaylistLimit = isFreePlan && playlistCount >= FREE_PLAN_LIMIT;
  const hasReachedAllAddLimits = hasReachedVideoLimit && hasReachedFileLimit && hasReachedPlaylistLimit;
  const addTabLimits = { video: hasReachedVideoLimit, file: hasReachedFileLimit, playlist: hasReachedPlaylistLimit };
  const addModalTab = ['video', 'file', 'playlist'].find((tab) => !addTabLimits[tab]) || 'video';

  const visiblePlaylists = useMemo(() => {
    const query = debouncedItemSearch.toLowerCase();
    return playlists.filter((playlist) => {
      if (query && !(playlist.title || '').toLowerCase().includes(query)) {
        return false;
      }
      if (matchingPlaylistIds && !matchingPlaylistIds.has(Number(playlist.id))) {
        return false;
      }
      return true;
    });
  }, [playlists, debouncedItemSearch, matchingPlaylistIds]);
  const limitedPlaylists = isFreePlan ? visiblePlaylists.slice(0, FREE_PLAN_LIMIT) : visiblePlaylists;
  const isSearching = Boolean(debouncedItemSearch);
  const shownPlaylists = isSearching ? limitedPlaylists : limitedPlaylists.slice(0, playlistPages * playlistPageSize);
  const shownVideos = isSearching ? limitedVideos : limitedVideos.slice(0, videoPages * itemPageSize);
  const shownFiles = isSearching ? limitedFiles : limitedFiles.slice(0, filePages * itemPageSize);
  const canSeeMorePlaylists = !isSearching && shownPlaylists.length < limitedPlaylists.length;
  const canSeeMoreVideos = !isSearching && (shownVideos.length < limitedVideos.length || (!isFreePlan && hasMoreVideos));
  const canSeeMoreFiles = !isSearching && (shownFiles.length < limitedFiles.length || (!isFreePlan && hasMoreFiles));

  const handleSeeMorePlaylists = () => {
    setPlaylistPages((pages) => pages + 1);
  };

  const handleSeeMoreVideos = async () => {
    if (loadingMoreVideos) return;
    const nextPages = videoPages + 1;
    const needed = nextPages * itemPageSize;
    if (!isFreePlan && videos.length < needed && hasMoreVideos) {
      setLoadingMoreVideos(true);
      try {
        await loadInspirationType('video', videoPage + 1, true);
      } catch (err) {
        console.error('Error loading more videos:', err);
      } finally {
        setLoadingMoreVideos(false);
      }
    }
    setVideoPages(nextPages);
  };

  const handleSeeMoreFiles = async () => {
    if (loadingMoreFiles) return;
    const nextPages = filePages + 1;
    const needed = nextPages * itemPageSize;
    if (!isFreePlan && files.length < needed && hasMoreFiles) {
      setLoadingMoreFiles(true);
      try {
        await loadInspirationType('file', filePage + 1, true);
      } catch (err) {
        console.error('Error loading more files:', err);
      } finally {
        setLoadingMoreFiles(false);
      }
    }
    setFilePages(nextPages);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    
    setDeleting(true);
    setError(null);
    
    try {
      if (itemToDelete.type === 'video') {
        await videosAPI.delete(itemToDelete.id);
      } else if (itemToDelete.type === 'file' || itemToDelete.type === 'image' || itemToDelete.type === 'pdf') {
        await filesAPI.delete(itemToDelete.id);
      }
      
      await fetchInspirations();
      await fetchPlaylists();
      
      setShowDeleteModal(false);
      setItemToDelete(null);
    } catch (err) {
      setError(err.data?.message || err.message || 'Failed to delete item');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleFavorite = async (item, e) => {
    e.stopPropagation();
    if (togglingFavorite === item.id) return;
    
    setTogglingFavorite(item.id);
    try {
      const isVideo = item.type === 'video';
      const api = isVideo ? videosAPI : filesAPI;
      const result = await api.toggleFavorite(item.id);
      
      // Update favorites state
      // Convert item.id to number to ensure type consistency
      const numericId = Number(item.id);
      const favoriteKey = `${isVideo ? 'video' : 'file'}-${numericId}`;
      setFavorites(prev => {
        const newFavorites = new Set(prev);
        if (result.is_favorited) {
          newFavorites.add(favoriteKey);
        } else {
          newFavorites.delete(favoriteKey);
        }
        return newFavorites;
      });
    } catch (err) {
      console.error('Error toggling favorite:', err);
      alert(err.data?.message || err.message || 'Failed to update favorite');
    } finally {
      setTogglingFavorite(null);
    }
  };

  const handleDeletePlaylist = async () => {
    if (!playlistToDelete) return;
    setDeleting(true);
    setError(null);
    try {
      await playlistsAPI.delete(playlistToDelete.id);
      await fetchPlaylists();
      await fetchInspirations();
      setShowPlaylistDeleteModal(false);
      setPlaylistToDelete(null);
    } catch (err) {
      setError(err.data?.message || err.message || 'Failed to delete playlist');
    } finally {
      setDeleting(false);
    }
  };


  const refreshLibraryListing = useCallback(async () => {
    await Promise.all([
      fetchInspirations(),
      fetchPlaylists(),
    ]);
  }, [fetchInspirations, fetchPlaylists]);

  const renderInspirationCard = (item, index) => {
    const favoriteKey = `${item.type === 'video' ? 'video' : 'file'}-${Number(item.id)}`;
    const isFavorited = favorites.has(favoriteKey);
    return (
      <HoverableCard
        key={`inspiration-${item.type}-${item.id}-${index}`}
        className="group relative"
      >
        <div className="w-full">
          {item.type === 'video' ? (
            <VideoThumbnail
              thumbnail={item.thumbnail}
              alt={item.title}
              className="group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="relative aspect-video bg-slate-100 overflow-hidden">
              <img
                src={item.thumbnail}
                alt={item.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                onError={(e) => {
                  e.target.src = 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=400&h=300&fit=crop';
                }}
              />
              {item.type === 'pdf' && (
                <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded text-xs font-medium">
                  PDF
                </div>
              )}
            </div>
          )}
          <div className="p-4">
            <div className="flex items-start justify-between mb-1">
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-slate-800 mb-1 line-clamp-2">{item.title}</h3>
                <p className="text-xs text-slate-500 capitalize">{item.type}</p>
                {item.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {item.tags.slice(0, 3).map((t) => (
                      <span key={t.id || t.tag} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-slate-100 text-slate-600">
                        <TagIcon icon={t.icon} size={14} />
                        {t.tag || t}
                      </span>
                    ))}
                    {item.tags.length > 3 && (
                      <span className="text-xs text-slate-400">+{item.tags.length - 3}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div
          className="absolute inset-0 z-10 bg-black bg-opacity-70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center gap-3 rounded-xl"
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              const type = item.type === 'video' ? 'video' : 'file';
              navigate(`/edit/inspiration/${type}/${item.id}`);
            }}
            className="w-40 min-h-10 text-xs px-3 py-2 bg-white text-slate-800 rounded-lg font-medium hover:bg-slate-100 transition-colors flex items-center justify-center gap-1.5"
            title="Edit"
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit
          </button>
          {(item.type === 'video' || item.type === 'image') && (
            <PrimaryButton
              onClick={(e) => {
                e.stopPropagation();
                if (item.type === 'video' && item.videoId) {
                  navigate(buildColorAlongVideoPath({
                    embedId: item.videoId,
                    pencilSetSizeId: item.pencilSetSizeId,
                    bookId: item.bookId,
                  }));
                } else if (item.type === 'image') {
                  navigate(`/color-along?image=${item.id}`);
                }
              }}
              className="w-40 min-h-10 justify-center"
              icon={
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              }
            >
              Color Along
            </PrimaryButton>
          )}
        </div>
        <div className="absolute top-2 right-2 z-20 flex items-center gap-2">
          <button
            onClick={(e) => handleToggleFavorite(item, e)}
            className={`p-2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full transition-all shadow-sm ${
              isFavorited ? 'text-red-500' : 'text-slate-600 hover:text-red-500'
            }`}
            title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
            disabled={togglingFavorite === item.id}
          >
            <svg className="w-4 h-4" fill={isFavorited ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setItemToDelete({ id: item.id, type: item.type === 'video' ? 'video' : 'file', title: item.title });
              setShowDeleteModal(true);
            }}
            className="p-2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full text-slate-600 hover:text-red-600 transition-all shadow-sm"
            title={`Delete ${item.type}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </HoverableCard>
    );
  };

  const renderPlaylistCard = (playlist) => {
    const playlistVideoCount = playlist.videos?.length ?? 0;
    const fallbackCount = Number.isFinite(Number(playlist.preview_total))
      ? Number(playlist.preview_total)
      : 0;
    const totalCount = playlistVideoCount || fallbackCount;
    return (
      <HoverableCard
        key={playlist.id}
        className="group cursor-pointer"
        onClick={() => navigate(`/studio/inspiration/playlists/${playlist.id}`)}
      >
        <div className="relative aspect-video bg-slate-100 overflow-hidden">
          <img
            src={playlist.thumbnail}
            alt={playlist.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              e.target.src = 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=400&h=300&fit=crop';
            }}
          />
        </div>
        <div className="p-4 space-y-2">
          <h3 className="font-semibold text-slate-800 line-clamp-2">{playlist.title}</h3>
          <p className="text-sm text-slate-500">
            {totalCount} {totalCount === 1 ? 'video' : 'videos'}
          </p>
          <div className="flex items-center justify-between pt-1">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setPlaylistToEdit(playlist);
                setEditPlaylistModalOpen(true);
              }}
              className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              title="Edit playlist name"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setPlaylistToDelete(playlist);
                setShowPlaylistDeleteModal(true);
              }}
              className="p-2 text-slate-600 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors"
              title="Delete playlist (videos are kept)"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      </HoverableCard>
    );
  };

  return (
    <div className="space-y-6">
      <div className="px-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-xl font-semibold text-slate-800 font-venti">Inspiration</h3>
            <p className="text-sm text-slate-600 mt-0.5">{STUDIO_SECTION_DESCRIPTIONS.inspiration}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <PrimaryButton
              onClick={() => {
                if (hasReachedAllAddLimits) {
                  alert('You\'ve reached the free plan limit of 5 videos, 5 other files, and 5 playlists. Please upgrade to Premium to add more.');
                  return;
                }
                setIsAddModalOpen(true);
              }}
              disabled={hasReachedAllAddLimits}
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              }
            >
              Add Inspiration
            </PrimaryButton>
          </div>
        </div>
      </div>

      <div className="bg-white p-6">
        <div className="flex items-center justify-end gap-2 flex-wrap mb-6">
          {knownTags.length > 0 && (
            <div ref={tagFilterDropdownRef} className="relative">
              <button
                type="button"
                onClick={() => setTagFilterDropdownOpen((o) => !o)}
                className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#ea3663]"
              >
                Tags
                {tagFilterIds.length > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: '#ea3663' }}>
                    {tagFilterIds.length}
                  </span>
                )}
                <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {tagFilterDropdownOpen && (
                <div className="absolute right-0 top-full mt-1 z-50 w-72 rounded-xl border border-slate-200 bg-white shadow-lg overflow-hidden">
                  <div className="p-2 border-b border-slate-100">
                    <input
                      type="text"
                      value={tagFilterSearch}
                      onChange={(e) => setTagFilterSearch(e.target.value)}
                      placeholder="Search tags..."
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#ea3663] focus:border-transparent"
                    />
                  </div>
                  <div className="max-h-56 overflow-y-auto py-1">
                    {knownTags
                      .filter((t) => !tagFilterSearch.trim() || (t.tag || '').toLowerCase().includes(tagFilterSearch.trim().toLowerCase()))
                      .map((t) => {
                        const isSelected = tagFilterIds.includes(Number(t.id));
                        return (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => {
                              setTagFilterIds((prev) => {
                                const id = Number(t.id);
                                if (prev.includes(id)) return prev.filter((x) => x !== id);
                                return [...prev, id];
                              });
                            }}
                            className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-slate-50 ${isSelected ? 'font-medium bg-slate-50' : 'text-slate-700'}`}
                            style={isSelected ? { color: '#ea3663' } : undefined}
                          >
                            <span className={`flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center ${isSelected ? '' : 'border-slate-300'}`} style={isSelected ? { backgroundColor: '#ea3663', borderColor: '#ea3663' } : undefined}>
                              {isSelected && (
                                <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </span>
                            <TagIcon icon={t.icon} size={18} />
                            {t.tag}
                          </button>
                        );
                      })}
                    {knownTags.filter((t) => !tagFilterSearch.trim() || (t.tag || '').toLowerCase().includes(tagFilterSearch.trim().toLowerCase())).length === 0 && (
                      <p className="px-3 py-4 text-sm text-slate-500 text-center">No tags match</p>
                    )}
                  </div>
                  {tagFilterIds.length > 0 && (
                    <div className="p-2 border-t border-slate-100">
                      <button
                        type="button"
                        onClick={() => setTagFilterIds([])}
                        className="w-full px-3 py-2 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
                        style={{ color: '#ea3663' }}
                      >
                        Clear all
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <input
            type="text"
            value={itemSearch}
            onChange={(e) => setItemSearch(e.target.value)}
            placeholder="Search inspiration..."
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-800 placeholder-slate-400 bg-white focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#ea3663] focus:border-transparent"
          />
        </div>
        {loading && videos.length === 0 && files.length === 0 && playlists.length === 0 ? (
          <div className="bg-white p-12 text-center">
            <div className="modern-loader mb-4">
              <div className="loader-ring">
                <div className="loader-ring-segment"></div>
                <div className="loader-ring-segment"></div>
                <div className="loader-ring-segment"></div>
                <div className="loader-ring-segment"></div>
              </div>
            </div>
            <h3 className="text-xl font-semibold text-slate-800 mb-2 font-venti">Loading Inspirations...</h3>
            <p className="text-slate-600">Fetching your inspiration library</p>
          </div>
        ) : error && videos.length === 0 && files.length === 0 && playlists.length === 0 && !loading ? (
          <ErrorState error={error} />
        ) : (
          <div className="space-y-8">
            <div id="inspiration-playlists">
              <h3 className="text-xl font-semibold text-slate-800 mb-4 font-venti border-b border-slate-200 pb-2">
                Playlists
              </h3>
              {hasReachedPlaylistLimit && (
                <div className="mb-4">
                  <UpgradeBanner itemType="playlists" />
                </div>
              )}
              {loadingPlaylists && limitedPlaylists.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-slate-500">Loading playlists...</p>
                </div>
              ) : limitedPlaylists.length === 0 && importing ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  <YouTubeImportPlaceholder count={3} />
                </div>
              ) : limitedPlaylists.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-slate-500">No playlists found.</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    <YouTubeImportPlaceholder />
                    {shownPlaylists.map(renderPlaylistCard)}
                  </div>
                  {canSeeMorePlaylists && (
                    <SeeMoreButton onClick={handleSeeMorePlaylists} />
                  )}
                </>
              )}
            </div>

            <div id="inspiration-videos">
              <h3 className="text-xl font-semibold text-slate-800 mb-4 font-venti border-b border-slate-200 pb-2">
                Videos
              </h3>
              {hasReachedVideoLimit && (
                <div className="mb-4">
                  <UpgradeBanner itemType="videos" />
                </div>
              )}
              {limitedVideos.length === 0 && importing ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                  <YouTubeImportPlaceholder count={4} />
                </div>
              ) : limitedVideos.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-slate-500">No videos found.</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                    <YouTubeImportPlaceholder />
                    {shownVideos.map(renderInspirationCard)}
                  </div>
                  {canSeeMoreVideos && (
                    <SeeMoreButton onClick={handleSeeMoreVideos} loading={loadingMoreVideos} />
                  )}
                </>
              )}
            </div>

            <div id="inspiration-files">
              <h3 className="text-xl font-semibold text-slate-800 mb-4 font-venti border-b border-slate-200 pb-2">
                Other Files
              </h3>
              {hasReachedFileLimit && (
                <div className="mb-4">
                  <UpgradeBanner itemType="other files" />
                </div>
              )}
              {limitedFiles.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-slate-500">No files found.</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                    {shownFiles.map(renderInspirationCard)}
                  </div>
                  {canSeeMoreFiles && (
                    <SeeMoreButton onClick={handleSeeMoreFiles} loading={loadingMoreFiles} />
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
      <AddInspirationModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setAddModalVideoMode(null);
        }}
        onSuccess={async () => {
          await refreshLibraryListing();
          setIsAddModalOpen(false);
        }}
        onPlaylistCreated={fetchPlaylists}
        defaultTab={addModalTab}
        defaultVideoMode={addModalVideoMode}
        videoLimitReached={hasReachedVideoLimit}
        fileLimitReached={hasReachedFileLimit}
        playlistLimitReached={hasReachedPlaylistLimit}
      />
      <EditPlaylistModal
        isOpen={editPlaylistModalOpen}
        onClose={() => { setEditPlaylistModalOpen(false); setPlaylistToEdit(null); }}
        onSuccess={() => { fetchPlaylists(); setEditPlaylistModalOpen(false); setPlaylistToEdit(null); }}
        playlist={playlistToEdit}
      />
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setItemToDelete(null);
        }}
        onConfirm={handleDelete}
        itemName={itemToDelete?.title || 'item'}
        itemType={itemToDelete?.type || 'item'}
      />
      <DeleteConfirmationModal
        isOpen={showPlaylistDeleteModal}
        onClose={() => { setShowPlaylistDeleteModal(false); setPlaylistToDelete(null); }}
        onConfirm={handleDeletePlaylist}
        itemName={playlistToDelete?.title || 'Playlist'}
        itemType="playlist"
        description="Videos in this playlist will not be deleted."
      />
    </div>
  );
};

export default Library;
